import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, X, PaperPlaneTilt, Robot } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "How much did we spend on fuel this month?",
  "Which supplier invoices are overdue?",
  "Show me the top 5 suppliers by pending PO value.",
  "How many items are below reorder level?",
  "What is our trial balance right now?",
  "How many tonnes did we produce in the last 30 days?",
];

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function AiAssistantDrawer() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  async function ask(text) {
    const question = (text || q).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question, at: new Date().toISOString() }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question });
      setMsgs((m) => [...m, {
        role: "ai",
        text: data.answer,
        tool: data.tool,
        sources: data.sources,
        at: data.at,
      }]);
    } catch (e) {
      toast.error(formatError(e));
      setMsgs((m) => [...m, { role: "ai", text: formatError(e), tool: null, sources: null, at: new Date().toISOString(), error: true }]);
    }
    setBusy(false);
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-fab"
          className="fixed bottom-6 right-6 z-40 bg-[#0b1027] hover:bg-black text-[#FFC000] shadow-2xl border border-[#FFC000]/30 px-5 py-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-transform hover:scale-105"
        >
          <Sparkle size={16} weight="fill" /> Ask LUROMA AI
        </button>
      )}

      {open && (
        <div
          className="fixed inset-y-0 right-0 z-50 w-full sm:w-[440px] bg-[#0b1027] text-white shadow-2xl flex flex-col"
          data-testid="ai-assistant-drawer"
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FFC000] text-[#0b1027] flex items-center justify-center">
                <Sparkle size={16} weight="fill" />
              </div>
              <div>
                <div className="font-heading font-black text-sm tracking-tight">LUROMA AI</div>
                <div className="phizo-label text-[9px] text-[#FFC000]">Grounded on your live data</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-assistant-close"
                    className="p-1 text-white/60 hover:text-white">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <p className="text-white/70 text-xs leading-relaxed">
                  Ask a business question — the assistant maps it to a safe internal
                  lookup, runs it, then answers using only what your data actually shows.
                </p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s, idx) => (
                    <button key={idx} type="button" onClick={() => ask(s)}
                            data-testid={`ai-suggest-${idx}`}
                            className="w-full text-left text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, idx) => (
              <div key={idx}>
                {m.role === "user" ? (
                  <div className="flex justify-end">
                    <div className="bg-[#FFC000]/15 border border-[#FFC000]/40 text-white px-3 py-2 max-w-[90%] text-xs"
                         data-testid={`ai-msg-user-${idx}`}>
                      {m.text}
                      <div className="text-[9px] text-white/40 mt-1 text-right">{fmtWhen(m.at)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 bg-white/10 flex items-center justify-center shrink-0 mt-1">
                      <Robot size={12} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`px-3 py-2 text-xs whitespace-pre-line ${m.error ? "bg-[#dc2626]/20 border border-[#dc2626]/40" : "bg-white/5 border border-white/10"}`}
                           data-testid={`ai-msg-ai-${idx}`}>
                        {m.text}
                      </div>
                      {m.tool && (
                        <details className="mt-1 text-[10px] text-white/50">
                          <summary className="cursor-pointer hover:text-white">
                            source: <span className="mono">{m.tool}</span>
                          </summary>
                          <pre className="mt-1 mono text-[10px] bg-black/40 p-2 overflow-x-auto whitespace-pre-wrap break-all">
                            {JSON.stringify(m.sources, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="flex items-center gap-2 text-white/60 text-xs">
                <Robot size={12} weight="bold" className="animate-pulse" />
                Thinking…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }}
                className="px-5 py-3 border-t border-white/10 flex items-center gap-2 bg-black/30">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Ask about fuel, POs, stock, payroll…"
                   disabled={busy}
                   className="flex-1 bg-white text-black text-sm px-3 py-2 outline-none"
                   data-testid="ai-input"/>
            <button type="submit" disabled={busy || !q.trim()}
                    data-testid="ai-send"
                    className="bg-[#FFC000] text-[#0b1027] px-3 py-2 disabled:opacity-50">
              <PaperPlaneTilt size={14} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
